// SPDX-License-Identifier: MIT
/**
 * Frame renderer: frame index → subframes (motion blur) → Canvas 2D → WebGL2 post → output canvas.
 * Same code path for server rendering (SwiftShader) and the browser Player.
 */
import { type Brand, type Reel, ReelSchema, reelDuration, reelFrameCount } from '@pulsereel/schema';
import { resolveBrand } from './brand.ts';
import { Compositor } from './compositor.ts';
import { PostPipeline, type PostQuality } from './post/pipeline.ts';
import { type FrameProbe, type LumaStats, TextProbe, frameHash, lumaStats } from './qa.ts';
import { DESIGN_H, DESIGN_W, type SceneClaim, type SceneModule, type SceneSoundEvent } from './scene.ts';
import { SCENE_KIT } from './scenes/index.ts';
import { getStyle } from './style.ts';
export const ENGINE_VERSION = '0.1.0';
export type RuntimeOptions = {
    /** Output canvas; receives the final composited frame through WebGL2. */
    canvas: HTMLCanvasElement | OffscreenCanvas;
    width: number;
    height: number;
    fps: number;
    /** Motion-blur subframes per output frame (1 = off). */
    subframes: number;
    /** Shutter as a fraction of the frame interval (0.5 = 180°). */
    shutter?: number;
    quality?: PostQuality;
    /** Blit frames to the canvas (default true). Pixel-reading hosts can disable it. */
    present?: boolean;
    /** Extra scene modules (e.g. agent-written custom scenes), merged over the scene kit. */
    scenes?: Record<string, SceneModule<never>>;
    /** QA session: instrument drawing so `qaFrame` can report text boxes (§7.10). */
    qa?: boolean;
};
export type QaFrame = FrameProbe & {
    luma: LumaStats;
    hash: string;
};
export type Runtime = {
    readonly duration: number;
    readonly frameCount: number;
    readonly glyphs: string;
    /** Sound requests from scenes, in global beats, for the soundtrack. */
    soundEvents(): SceneSoundEvent[];
    /** What the scenes claim about the product (`SceneModule.claims`), for QA. */
    claims(): SceneClaim[];
    renderFrame(frame: number): void;
    /** Sub-frame-free render of an arbitrary time (stills, scrubbing). */
    renderTime(t: number): void;
    /** Last frame as RGBA, rows bottom-up. */
    readRgba(dst: Uint8Array): void;
    /** Last frame as planar yuv420p (BT.709 limited range). */
    readYuv420(dst: Uint8Array): void;
    readonly yuv420Bytes: number;
    /** QA sessions only: render `frame`, returning captured text, reported elements and luminance. */
    qaFrame(frame: number): QaFrame;
    /** Draw the 2D frame at `t` without post (for timing scene code). */
    drawOnly(t: number): void;
    /**
     * Swap in an edited reel and/or brand (live editing in the Player). Keeps the GL pipeline and
     * canvases; size and fps stay as created. Throws, leaving the old reel in place, if the reel is invalid.
     */
    setReel(reel: Reel, brand?: Brand): void;
};
function webgl2(canvas: HTMLCanvasElement | OffscreenCanvas, attrs: WebGLContextAttributes) {
    return canvas instanceof OffscreenCanvas
        ? canvas.getContext('webgl2', attrs)
        : canvas.getContext('webgl2', attrs);
}
export function createRuntime(reelInput: Reel, brandInput: Brand, opts: RuntimeOptions): Runtime {
    const { width, height, fps } = opts;
    const subframes = Math.max(1, Math.floor(opts.subframes));
    const shutter = opts.shutter ?? 0.5;
    const gl = webgl2(opts.canvas, {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        premultipliedAlpha: false,
        preserveDrawingBuffer: true,
    });
    if (!gl)
        throw new Error('WebGL2 unavailable');
    opts.canvas.width = width;
    opts.canvas.height = height;
    const scratch = new OffscreenCanvas(width, height);
    const ctx = scratch.getContext('2d', { alpha: false }) as unknown as CanvasRenderingContext2D | null;
    if (!ctx)
        throw new Error('Canvas 2D unavailable');
    const scenes = { ...SCENE_KIT, ...opts.scenes };
    const build = (reelIn: Reel, brandIn: Brand) => {
        const reel = ReelSchema.parse(reelIn);
        const style = getStyle(reel.style);
        const compositor = new Compositor({
            reel,
            brand: resolveBrand(brandIn),
            style,
            scenes,
            size: [width, height],
        });
        const duration = reelDuration(reel);
        return {
            brandIn,
            style,
            compositor,
            duration,
            frameCount: reelFrameCount(reel, fps),
            lastT: duration - 1e-6,
        };
    };
    let cur = build(reelInput, brandInput);
    const post = new PostPipeline(gl, width, height, {
        quality: opts.quality ?? 'full',
        present: opts.present ?? true,
    });
    // Crisp layer: a transparent canvas, cleared lazily the first time a scene draws into it.
    const crispCanvas = new OffscreenCanvas(width, height);
    const crispCtx = crispCanvas.getContext('2d') as unknown as CanvasRenderingContext2D | null;
    if (!crispCtx)
        throw new Error('Canvas 2D unavailable');
    let crispUsed = false;
    const crisp = {
        acquire() {
            if (!crispUsed) {
                crispCtx.reset();
                crispCtx.setTransform(width / DESIGN_W, 0, 0, height / DESIGN_H, 0, 0);
                crispUsed = true;
            }
            return crispCtx;
        },
    };
    const probe = opts.qa ? new TextProbe([width, height]) : null;
    if (probe) {
        probe.instrument(ctx);
        probe.instrument(crispCtx);
    }
    const rgba = opts.qa ? new Uint8Array(width * height * 4) : null;
    const renderAt = (t: number, frame: number, n: number, capture?: TextProbe) => {
        const { compositor, style, lastT } = cur;
        post.beginFrame();
        for (let s = 0; s < n; s++) {
            const ts = n === 1 ? t : t + ((s + 0.5) / n - 0.5) * (shutter / fps);
            crispUsed = false;
            // QA captures the subframe at the centre of the shutter, i.e. the frame's own time.
            compositor.draw(ctx, Math.min(Math.max(ts, 0), lastT), s === n >> 1 ? capture : undefined, crisp);
            post.accumulate(scratch, 1 / n);
            if (crispUsed)
                post.accumulateOverlay(crispCanvas, 1 / n);
        }
        post.finish(style.post, compositor.fx(t), frame);
    };
    return {
        get duration() {
            return cur.duration;
        },
        get frameCount() {
            return cur.frameCount;
        },
        get glyphs() {
            return cur.compositor.glyphs();
        },
        soundEvents: () => cur.compositor.soundEvents(),
        claims: () => cur.compositor.claims(),
        renderFrame(frame) {
            renderAt(frame / fps, frame, subframes);
        },
        renderTime(t) {
            renderAt(Math.min(Math.max(t, 0), cur.lastT), Math.round(t * fps), 1);
        },
        readRgba: (dst) => post.readRgba(dst),
        readYuv420: (dst) => post.readYuv420(dst),
        yuv420Bytes: post.yuv420Bytes,
        qaFrame(frame) {
            if (!probe || !rgba)
                throw new Error('qaFrame needs a runtime created with { qa: true }');
            probe.begin();
            try {
                renderAt(frame / fps, frame, subframes, probe);
            }
            catch (err) {
                probe.end();
                throw err;
            }
            const captured = probe.end();
            post.readRgba(rgba);
            return { ...captured, luma: lumaStats(rgba, width, height), hash: frameHash(rgba) };
        },
        drawOnly(t) {
            crispUsed = false;
            cur.compositor.draw(ctx, Math.min(Math.max(t, 0), cur.lastT), undefined, crisp);
            // Canvas 2D defers rasterisation; reading one pixel makes the timing include it.
            ctx.getImageData(0, 0, 1, 1);
            if (crispUsed)
                crispCtx.getImageData(0, 0, 1, 1);
        },
        setReel(reel, brand) {
            cur = build(reel, brand ?? cur.brandIn);
        },
    };
}
